import { CountryCode, Currency, Timezone, BankAccountType, AddressSchema, } from '../index.js';
import { CustomerCareSchema } from '../customer-care/index.js';
import { CaseMergeRulesSchema } from '../customers/index.js';
import { PaymentPlansConfigSchema, } from '../economy/payment-plans/index.js';
import { Lang } from '../lang/Lang.js';
import { PerformedBySchema } from '../performed-by/index.js';
import { z } from 'zod/v4';
import { TribunalCaseType } from '../tribunal/cases/types.js';
export var TenantType;
(function (TenantType) {
    TenantType["AccountsReceivable"] = "ACCOUNTS_RECEIVABLE";
    TenantType["Agency"] = "AGENCY";
})(TenantType || (TenantType = {}));
export var ReferenceIdType;
(function (ReferenceIdType) {
    ReferenceIdType["CustomerReferenceId"] = "CUSTOMER_REFERENCE_ID";
    ReferenceIdType["CaseVoucherReferenceId"] = "CASE_VOUCHER_REFERENCE_ID";
})(ReferenceIdType || (ReferenceIdType = {}));
const TribunalSettingsSchema = z.object({
    courtRepresentative: z.string().optional(),
    courtPhone: z.string().optional(),
    courtEmail: z.string().optional(),
    courtRegistrationNumber: z.string().optional(),
    courtAccountNumber: z.string().optional(),
    caseTypes: z
        .object({
        [TribunalCaseType.DKBetalingspaakrav]: z
            .object({
            overrideCourtCostOnCase: z.boolean().optional(),
            courtCostVoucherTypeId: z.uuid().optional(),
            subtractVoucherTypeIdsFromCourtCost: z.array(z.uuid()).optional(),
            addInterestOnVoucherTypeIdsAfterVerdict: z.array(z.uuid()).optional(),
            maximizeCollectionCost: z.boolean().optional(),
            showCPRInBpk: z.boolean().optional(),
            treatOrientationsAsSummons: z.boolean().optional(),
        })
            .optional(),
    })
        .optional(),
});
export var TenantStatus;
(function (TenantStatus) {
    TenantStatus["GettingStarted"] = "GETTING_STARTED";
    TenantStatus["Demo"] = "DEMO";
    TenantStatus["Active"] = "ACTIVE";
    TenantStatus["Sandbox"] = "SANDBOX";
    TenantStatus["Disabled"] = "DISABLED";
})(TenantStatus || (TenantStatus = {}));
export var ErpVendorType;
(function (ErpVendorType) {
    ErpVendorType["EConomic"] = "E_CONOMIC";
})(ErpVendorType || (ErpVendorType = {}));
export const TenantStatusSchema = z.enum(TenantStatus);
export const PriceListSchema = z.object({
    caseCloseFee: z.number().int().nullish(),
    clientCaseCreationFee: z.number().int().nullish(),
    tenantCaseCreationFee: z.number().int().nullish(),
    RKIRegistrationFee: z.number().int().nullish(),
    paymentDemandFee: z.number().int().nullish(),
    fogedrekvisitionsFee: z.number().int().nullish(),
    folkeregisteropslagsFee: z.number().int().nullish(),
    anmeldelseAfKravFee: z.number().int().nullish(),
    legalAdviceHourlyRate: z.number().int().nullish(),
    afslysningAfUdlaeg: z.number().int().nullish(),
});
export const AKSSettingsSchema = z.union([
    z.object({
        key: z.string(),
        code: z.string(),
        password: z.string(),
        username: z.string(),
        limit: z.number().optional(),
    }),
    z.object({}),
]);
export const ServerSetupSchema = z.object({
    type: z.enum(['SFTP', 'FTP']),
    data: z.object({
        user: z.string(),
        host: z.string(),
        password: z.string(),
        port: z.number(),
    }),
});
export const SettingsSchema = z.object({
    autoImposeCompensationCost: z.boolean().nullish(),
    autoSendInteractionsAfterXVerifications: z
        .number()
        .min(0)
        .max(9999)
        .nullish(),
    preventSendingLetters: z.boolean().nullish(),
    preventAttachingBillingsToBillingEmails: z.boolean().nullish(),
    disableWorkflows: z.boolean().nullish(),
    paymentPlanFoundationTemplateId: z.uuid().nullish(),
    selfCollection: z.boolean().nullish(),
    twoFactorRequired: z.boolean().nullish(),
    aiDisabled: z
        .boolean()
        .nullish()
        .transform((x) => x ?? undefined),
    legisIntegration: z
        .boolean()
        .nullish()
        .transform((x) => x ?? undefined),
    isPayoutTenant: z
        .boolean()
        .nullish()
        .transform((x) => x ?? undefined),
    outboundCall: z.boolean().nullish(),
    autoDebtRegister: z.boolean().nullish(),
    vatConnectorId: z.uuid().nullish(),
    autoVerifyCases: z.boolean().nullish(),
    advokatindestaaelse: z.boolean().nullish(),
    autoRKIOpen: z.boolean().nullish(),
    autoRKIIgnoreUnresolvedOppositions: z.boolean().nullish(),
    autoRKIDeleteOnCaseEnd: z.boolean().nullish(),
    responsibleForUpdateTypes: z.any().nullish(),
    accountingExportSettings: z.any().nullish(),
    letterSetup: z
        .object({
        returnAddress: AddressSchema,
        server: ServerSetupSchema.optional(),
    })
        .nullish(),
    referenceIdFinders: z
        .array(z.object({
        referenceIdType: z.enum(ReferenceIdType),
        referenceIdLength: z.number(),
        referenceIdMin: z.number().nullish(),
        referenceIdMax: z.number().nullish(),
    }))
        .optional(),
    tribunal: TribunalSettingsSchema.optional(),
    debtorweb: z
        .object({
        contactUsText: z.string().optional(),
        welcomeText: z.string().optional(),
        hideContactInformation: z.boolean().optional(),
        noPartialPayments: z.boolean().optional(),
        doNotEncourageAPaymentPlan: z.boolean().optional(),
    })
        .optional(),
    billingTextFields: z
        .object({
        underCreditorName: z.string().nullish(),
        endWhenReceivingPayment: z.string().nullish(),
        endWhenSendingPayment: z.string().nullish(),
    })
        .nullish()
        .transform((x) => x ?? undefined),
    workflows: z
        .object({
        executionInterval: z
            .object({
            fromHour: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            fromMinute: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            toHour: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
            toMinute: z
                .number()
                .int()
                .nullish()
                .transform((x) => x ?? undefined),
        })
            .optional(),
    })
        .optional(),
    customCaseVoucherTables: z.any().optional(),
    userOverrides: z
        .array(z.object({
        key: z.string(),
        match: z.object({
            cvr: z
                .string()
                .nullish()
                .transform((x) => x ?? undefined),
        }),
        set: z.object({
            userEmail: z
                .string()
                .nullish()
                .transform((x) => x ?? undefined),
            customerTag: z
                .string()
                .nullish()
                .transform((x) => x ?? undefined),
        }),
    }))
        .optional(),
});
const TenantBillingEmailsSchema = z.object({
    to: z.string().nullish(),
    cc: z.string().nullish(),
    bcc: z.string().nullish(),
});
export const UpdateSchema = z.object({
    name: z.string().optional(),
    type: z.enum(TenantType).optional(),
    creditorId: z.uuid().optional(),
    countryCode: z.string().optional(),
    currency: z.string().optional(),
    collectorIdentityId: z.uuid().optional(),
    defaultWorkflowId: z.uuid().optional(),
    defaultBillingModelId: z.uuid().nullish(),
    defaultBillingAppendixSetupId: z.uuid().nullish(),
    status: TenantStatusSchema.optional(),
    economicToken: z.string().optional(),
    address: AddressSchema.nullish(),
    priceList: PriceListSchema.optional(),
    settings: SettingsSchema.optional(),
    embeds: z.any().optional(),
    defaultProcedures: z.any().optional(),
    billingPriceList: z.any().optional(),
    billingEnabled: z.boolean().optional(),
    referenceId: z.string().optional(),
    vatNumber: z.string().optional(),
    paymentTerms: z.any().optional(),
    invoicingEmails: TenantBillingEmailsSchema.optional(),
    caseMergeRules: CaseMergeRulesSchema.nullish(),
    paymentPlansConfig: PaymentPlansConfigSchema.nullish(),
    customerCare: CustomerCareSchema.nullish(),
    credits: z.any().optional(),
    aksSettings: AKSSettingsSchema.optional(),
    caseGroupWorkflows: z.any().nullish(),
    bankAccountDetails: z
        .object({
        type: z.enum(BankAccountType),
        bankName: z.string(),
        registrationNumber: z.string(),
        accountNumber: z.string(),
        createdAt: z.string(),
    })
        .nullish(),
    erpVendor: z
        .object({
        type: z.enum(ErpVendorType),
        token: z.string(),
    })
        .nullish(),
    featureFlags: z.any().optional(),
});
export const CreateSchema = z.object({
    type: z.enum(TenantType),
    creditorId: z
        .string()
        .uuid()
        .nullish()
        .transform((x) => x ?? undefined),
    id: z.uuid().optional(),
    name: z.string(),
    address: z.string().nullish(),
    referenceId: z.string().nullish(),
    vatNumber: z.string().nullish(),
    countryCode: z.enum(CountryCode),
    currency: z.enum(Currency),
    lang: z.enum(Lang),
    timezone: z.enum(Timezone),
    collectorIdentityId: z.uuid(),
    defaultWorkflowId: z.uuid().optional(),
    defaultBillingModelId: z.uuid().nullish(),
    defaultBillingAppendixSetupId: z.uuid().nullish(),
    status: TenantStatusSchema.optional(),
    economicToken: z.string().optional(),
    createdBy: PerformedBySchema.nullish(),
    defaultProcedures: z.any().optional(),
});
export var TenantMockDataType;
(function (TenantMockDataType) {
    TenantMockDataType["PreCollection"] = "PRE_COLLECTION";
    TenantMockDataType["DebtCollection"] = "DEBT_COLLECTION";
})(TenantMockDataType || (TenantMockDataType = {}));
//# sourceMappingURL=index.js.map